import { trackEvent } from 'helpers/analytics';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { activeNetworkSelector, userSelector } from 'redux/selectors';
import { useCallback, useState } from 'react';
import { toggleJobSelection } from 'hooks/useJobs/actions';
import useJobRules from 'hooks/jobRules/useJobRules';
import { useTierGating } from 'hooks/useTierGating';
import { resolveFindemUserId } from 'services/tierGating';
import { useHeaderSelectAll } from './useHeaderSelectAll';

export const useJobTableFunctions = ({ onSort, jobs }) => {
  const history = useHistory();
  const [showJobRules, setShowJobRules] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradePaywallOrigin, setUpgradePaywallOrigin] = useState(null);
  const [showNoAccessModal, setShowNoAccessModal] = useState(false);
  const [noAccessJob, setNoAccessJob] = useState(null);
  const activeNetwork = useSelector(activeNetworkSelector);
  const user = useSelector(userSelector);
  // Findem identity is per network: the network's override wins over the global one.
  const findemUserId = resolveFindemUserId(activeNetwork, user);
  const dispatch = useDispatch();
  const { loadJobRulesRequest } = useJobRules();
  const { hasCapability } = useTierGating();

  const { onSelectAllClick, selectAllJobs } = useHeaderSelectAll();

  const dismissUpgradeModal = useCallback(() => {
    setShowUpgradeModal(false);
    setUpgradePaywallOrigin(null);
  }, []);

  const dismissNoAccessModal = useCallback(() => {
    setShowNoAccessModal(false);
    setNoAccessJob(null);
  }, []);

  const onFindCandidates = useCallback(
    (job) => {
      if (!hasCapability('people_finder')) {
        setUpgradePaywallOrigin('find_candidates');
        setShowUpgradeModal(true);
        return;
      }

      trackEvent('jobs:find_candidates_click', {
        networkId: activeNetwork.id,
        jobId: job.id,
        organizationId: job.organization?.id,
      });

      const guidedParams = new URLSearchParams();
      if (job.originalJobPostUrl) {
        guidedParams.set('prompt', job.originalJobPostUrl);
      }
      guidedParams.set('getroJobId', String(job.id));
      const guidedPath = `/new-search/guided?${guidedParams.toString()}`;
      history.push(`/networks/${activeNetwork.slug}/projects?url=${encodeURIComponent(guidedPath)}`);
    },
    [activeNetwork.id, activeNetwork.slug, hasCapability, history],
  );

  const onOpenProject = useCallback(
    (job) => {
      if (!hasCapability('people_finder')) {
        setUpgradePaywallOrigin('open_project');
        setShowUpgradeModal(true);
        return;
      }

      const userIds = job.findemProjectUserIds || [];
      const hasAccess = Boolean(findemUserId) && userIds.includes(findemUserId);
      if (!hasAccess) {
        trackEvent('jobs:see_people_finder_project:no_access', {
          jobId: job.id,
          organizationId: job.organization?.id,
        });
        setNoAccessJob(job);
        setShowNoAccessModal(true);
        return;
      }

      trackEvent('jobs:see_people_finder_project', {
        networkId: activeNetwork.id,
        jobId: job.id,
        findemProjectId: job.findemProjectId,
      });

      const projectParams = new URLSearchParams({
        joid: job.findemProjectOwnerId,
        redirect_joid: findemUserId,
        tab: 'search',
        channel: 'external',
      });
      const projectPath = `/searches/${job.findemProjectId}?${projectParams.toString()}`;
      history.push(`/networks/${activeNetwork.slug}/projects?url=${encodeURIComponent(projectPath)}`);
    },
    [activeNetwork.id, activeNetwork.slug, hasCapability, history, findemUserId],
  );

  const onSelect = useCallback(
    (job, checked, isAll) => {
      if (isAll) {
        onSelectAllClick();
      } else {
        dispatch(toggleJobSelection({ job, checked }));
      }
    },
    [onSelectAllClick, dispatch],
  );

  const handleSort = useCallback(
    (args) => {
      onSort(args);
      const [{ desc, column }] = args;

      trackEvent('table:sort', {
        networkId: activeNetwork.id,
        page: 'jobs',
        type: desc ? 'desc' : 'asc',
        columnName: column.title,
      });
    },
    [onSort, activeNetwork],
  );

  const onColumnAction = useCallback(
    (operation, { payload: column }) => {
      trackEvent('table:change_columns', {
        networkId: activeNetwork.id,
        page: 'jobs',
        operation,
        columnName: column.title,
      });
    },
    [activeNetwork],
  );

  const getJobById = useCallback((job) => jobs.find((j) => parseInt(j.id, 10) === parseInt(job?.id, 10)) ?? {}, [jobs]);

  const isJobSourcePostedActiveOrExpired = useCallback(
    (e) => {
      const job = getJobById(e);
      if (job.source === 'posted') {
        if (job.status === 'active' || job.status === 'expired') {
          return false;
        }
      }
      return true;
    },
    [getJobById],
  );

  const onShowJobRulesClick = useCallback(() => {
    loadJobRulesRequest();
    setShowJobRules(true);
  }, [loadJobRulesRequest]);

  return {
    onFindCandidates,
    onOpenProject,
    onSelect,
    handleSort,
    onColumnAction,
    selectAllJobs,
    getJobById,
    isJobSourcePostedActiveOrExpired,
    showJobRules,
    onShowJobRulesClick,
    setShowJobRules,
    showUpgradeModal,
    upgradePaywallOrigin,
    dismissUpgradeModal,
    showNoAccessModal,
    noAccessJob,
    dismissNoAccessModal,
  };
};
